import { cn } from '../lib/cn';

export interface ModernSegment<T extends string = string> {
  value: T;
  label: string;
  icon?: React.ReactNode;
}

/**
 * ModernSegmentedControl — compact pill switch for 2–4 mutually exclusive
 * options (view modes, filters, the design-system toggle, …). Quiet track,
 * single raised thumb on the active option.
 */
export function ModernSegmentedControl<T extends string = string>({
  segments,
  value,
  onChange,
  size = 'md',
  className,
  'aria-label': ariaLabel,
}: {
  segments: ModernSegment<T>[];
  value: T;
  onChange: (value: T) => void;
  size?: 'sm' | 'md';
  className?: string;
  'aria-label'?: string;
}) {
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={cn(
        'inline-flex items-center gap-1 rounded-full border border-border bg-muted/50 p-1',
        className,
      )}
    >
      {segments.map((segment) => {
        const active = segment.value === value;
        return (
          <button
            key={segment.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(segment.value)}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full font-medium transition-colors',
              size === 'sm' ? 'h-7 px-3 text-[12px]' : 'h-8 px-3.5 text-[13px]',
              active
                ? 'bg-card text-foreground shadow-[var(--mui-shadow-card)]'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {segment.icon}
            {segment.label}
          </button>
        );
      })}
    </div>
  );
}
